import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminLocations() {
  const [locations, setLocations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    area: '',
    address: '',
    map_link: '',
    image: '',
    description: '',
    features: '',
    latitude: '',
    longitude: '',
    map_position_top: '',
    map_position_left: '',
    is_active: true
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await apiClient.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        features: formData.features.split(',').map(f => f.trim()),
        id: editingLocation?.id || undefined
      };

      if (editingLocation) {
        await apiClient.put(`/admin/locations/${editingLocation.id}`, payload);
        toast.success('Location updated successfully!');
      } else {
        await apiClient.post('/admin/locations', payload);
        toast.success('Location created successfully!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error('Failed to save location');
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      ...location,
      features: location.features.join(', ')
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await apiClient.delete(`/admin/locations/${id}`);
        toast.success('Location deleted successfully!');
        fetchLocations();
      } catch (error) {
        toast.error('Failed to delete location');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      city: '',
      area: '',
      address: '',
      map_link: '',
      image: '',
      description: '',
      features: '',
      is_active: true
    });
    setEditingLocation(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Manage Locations</h1>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Location
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <div key={location.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <img src={location.image} alt={location.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{location.name}</h3>
                <p className="text-gray-600 text-sm mb-2">{location.area}, {location.city}</p>
                <p className="text-gray-500 text-sm mb-4">{location.description}</p>
                <div className="flex space-x-2">
                  <Button onClick={() => handleEdit(location)} size="sm" variant="outline">
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => handleDelete(location.id)} size="sm" variant="destructive">
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLocation ? 'Edit Location' : 'Add New Location'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Location Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="The Circuit Sarjapur"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Bangalore"
                  required
                />
              </div>
              <div>
                <Label>Area</Label>
                <Input
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  placeholder="Sarjapur"
                  required
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address"
                required
              />
            </div>
            <div>
              <Label>Google Maps Link</Label>
              <Input
                value={formData.map_link}
                onChange={(e) => setFormData({ ...formData, map_link: e.target.value })}
                placeholder="https://maps.google.com/..."
                required
              />
            </div>
            <ImageUpload
              label="Location Image"
              value={formData.image}
              onChange={(url) => setFormData({ ...formData, image: url })}
              required={true}
              preview={true}
            />
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
                required
              />
            </div>
            <div>
              <Label>Features (comma separated)</Label>
              <Input
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="25,000 sq. ft., Premium Location, Tech Hub"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save Location
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
